import clsx from 'clsx';
import CurrencyIcon from 'components/CurrencyIcon/CurrencyIcon';
import { Product, useProduct, useProducts } from 'hooks/products/useProducts';
import { useState } from 'react';
import colors from 'styles/colors';

import { TimeFrameKey, timeFrames } from 'data/time-frames';
import { BundleAsset, BundleInfo } from 'store/slices/types/BundleInfo';
import { PieChart } from './PieChart';
import { Typography } from '@altify/components';
import { useGetAssetsQuery } from 'store/slices/references-api';

const PieContents = ({ asset }: { asset: BundleAsset }) => {
  const { product } = useProduct(asset?.code);
  const { data: assets } = useGetAssetsQuery();
  const referenceAsset = assets?.find(a => a.code === asset?.code);
  const productName =
    product?.name ??
    referenceAsset?.name ??
    product?.code;

  return (
    <div className="mx-auto px-2">
      <div className="flex-row items-center justify-center relative z-10">
        <Typography size="medium" className="text-neutral-300 text-center">
          {product?.code ?? asset?.code}
        </Typography>
        <Typography size="large" className="text-white text-center">
          {productName}
        </Typography>
      </div>
    </div>
  );
};

const ConstituentsList = ({
  assets,
  activeAsset,
  onPressItem,
}: {
  activeAsset: string;
  assets: BundleAsset[];
  onPressItem?: (code: string) => void;
}) => {
  return (
    <div className="flex flex-wrap items-center relative z-10 md:mt-3">
      {assets?.map(asset => (
        <ConstituentsListItem
          asset={asset}
          isActive={asset?.code === activeAsset}
          onPressItem={() => onPressItem?.(asset?.code)}
          assetCount={assets.length}
        />
      ))}
    </div>
  );
};

function ConstituentsListItem({
  asset,
  isActive,
  onPressItem,
  assetCount,
}: {
  asset: BundleAsset;
  isActive: boolean;
  assetCount: number;
  onPressItem?: () => void;
}) {
  const { product } = useProduct(asset?.code);
  const { data: assets } = useGetAssetsQuery();
  const referenceAsset = assets?.find(a => a.code === asset?.code);

  const productName =
    product?.name ??
    referenceAsset?.name ??
    product?.code;
  const productColor = product?.colors?.constituent ?? product?.colors?.primary  ?? referenceAsset?.colors?.primary;
  return (
    <div
      className="flex flex-grow basis-full mb-2 sm:mb-4 cursor-pointer gap-2"
      onClick={() => onPressItem?.()}>
      <div
        className={`flex flex-row flex-grow py-2 px-2 items-center justify-center md:justify-start w-full mx-1  ${
          isActive ? 'sm:bg-blue-grey sm:rounded-lg' : ''
        }`}>
        <div className="flex items-center justify-center">
          <div className="flex items-center justify-center rounded-full mr-2">
            <CurrencyIcon
              currencyCode={product?.code ?? asset?.code}
              className="h-14 w-14 xxs:w-8 xxs:h-8 xs:w-12 xs:h-12"
              inactive={false}
              color={productColor}
            />
          </div>
          <div className="flex flex-row items-center justify-center relative z-10">
            <div className="flex flex-col items-start justify-start relative z-10">
              <Typography size="large" className="text-white text-left">
                {productName}
              </Typography>
              <Typography size="medium" className="text-neutral-300 text-left">
                {product?.code ?? asset?.code}
              </Typography>
            </div>
          </div>
        </div>
        <div className="flex flex-grow items-end justify-end">
          <div className="flex flex-row items-center justify-center relative z-10">
            <div className="flex flex-col items-start justify-start relative z-10">
              {asset?.currentWeight && (
                <Typography
                  size="medium"
                  className="text-neutral-300 text-left">
                  {`${Number(asset?.currentWeight).toFixed(2)}%`}
                </Typography>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const chartWidth = 160;
const chartHeight = 160;
const strokeWidth = 1;
const chartRadius = 69;

const ConstituentsPie = ({
  noPadding = false,
  bundle,
}: {
  noPadding?: boolean;
  bundle: BundleInfo;
}) => {
  const { products } = useProducts();
  const sortedAssets = bundle?.assets?.slice()
    .sort((a, b) => (a.currentWeight > b.currentWeight ? -1 : 1));

  const [selectedAssetCode, setSelectedAssetCode] = useState(
    sortedAssets?.[0]?.code,
  );
  const selectedAsset = bundle?.assets?.find(
    asset => asset?.code === selectedAssetCode,
  )!;

  
  
  const pieData = assetsToPie(sortedAssets || [], products);
  
  const pieColors = pieData?.map(asset => {
    if (asset.x === selectedAssetCode) {
      return asset.color;
    }
    return colors['blue-grey'];
  });

  const definedPieColors = pieColors.map(
    color => color || colors['vivid-blue'],
  );

  return (
    <div
      className={`${noPadding ? '' : 'p-4'} pr-0 items-center lg:items-start justify-center flex flex-col lg:flex-row justify-between mt-8`}>
      <div className="relative items-center justify-center basis-full lg:basis-[49.5%]">
        <PieChart
          data={pieData}
          colors={definedPieColors}
          strokeWidth={strokeWidth}
          width={chartWidth}
          height={chartHeight}
          size={chartWidth}
          radius={chartRadius}
          activeSegment={selectedAssetCode}
          zIndex={1}
          animate={false}
          classes="max-w-screen w-full"
          content={<PieContents asset={selectedAsset} />}
          animationDuration={0}
          hover={false}
        />
      </div>
      {bundle && (
        <div className="relative items-center justify-center  basis-full lg:basis-[49%] mt-2">
          <ConstituentsList
            assets={sortedAssets}
            activeAsset={selectedAssetCode}
            onPressItem={setSelectedAssetCode}
          />
        </div>
      )}
    </div>
  );
};

const assetsToPie = (
  assets: BundleAsset[] | never | null,
  list: Product[] | null,
) => {
  if (!assets) return [];

  return assets.map(asset => {
    const product = list?.find(p => p.code === asset.code);
    return {
      x: product?.code,
      y: (Number(asset.currentWeight) / assets.length) * 10,
      color:
        product?.colors?.constituent ||
        product?.colors?.primary ||
        colors['vivid-blue'],
    };
  });
};
export default ConstituentsPie;
